import React from "react";
import {LucideIcon, Loader2} from "lucide-react";
import {cn} from "./cn";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "ghost" | "danger" | "danger-ghost";
    size?: "sm" | "md";
    icon?: LucideIcon;
    iconPosition?: "left" | "right";
    loading?: boolean;
}

const base =
    "inline-flex items-center justify-center font-semibold transition-colors duration-150 " +
    "disabled:opacity-50 disabled:cursor-not-allowed " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

const variants: Record<NonNullable<ButtonProps["variant"]>, string> = {
    primary: "bg-primary text-primary-fg shadow-xs hover:bg-primary-hover",
    secondary: "bg-surface text-ink border border-border shadow-xs hover:bg-surface-hover hover:border-border-strong",
    ghost: "text-ink-muted hover:text-ink hover:bg-surface-hover",
    danger: "bg-error text-white shadow-xs hover:bg-error-hover",
    "danger-ghost": "text-error hover:bg-error-tint",
};

const sizes: Record<NonNullable<ButtonProps["size"]>, string> = {
    sm: "h-8 px-3 text-xs rounded-lg gap-1.5",
    md: "h-10 px-4 text-sm rounded-xl gap-2",
};

const iconSizes: Record<NonNullable<ButtonProps["size"]>, number> = {
    sm: 14,
    md: 16,
};

export const Button: React.FC<ButtonProps> = ({
                                                    variant = "primary",
                                                    size = "md",
                                                    icon: Icon,
                                                    iconPosition = "left",
                                                    loading,
                                                    disabled,
                                                    className,
                                                    children,
                                                    ...props
                                                }) => {
    const iconSize = iconSizes[size];
    return (
        <button
            disabled={disabled || loading}
            className={cn(base, variants[variant], sizes[size], className)}
            {...props}
        >
            {loading ? (
                <Loader2 size={iconSize} className="animate-spin"/>
            ) : (
                Icon && iconPosition === "left" && <Icon size={iconSize}/>
            )}
            {children}
            {!loading && Icon && iconPosition === "right" && <Icon size={iconSize}/>}
        </button>
    );
};
